import { FC, useCallback, useMemo, useState } from 'react';
import { Views, Calendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import './styles.scss';
import { theme } from '../../../styles/theme';
import moment from 'moment';
import 'moment-timezone';
import { Box, darken, useMediaQuery } from '@mui/material';
// Models, Helpers
import { ReactSetState } from '../../../models';
import { handleStateUpdateWithoutSave } from '../../../helpers';
import { IClosingCalendarEvents, IGetCalendarEvent } from '../../../models/calendar';
// Components
import { Loader } from '../../../components';
import WorkWeekMonthView from './custom-month-view';
import { ConfirmModal } from './closing-calendar-confirm-modal';
import { CustomHeader } from './custom-header/closing-calendar-header';
import { ClosingCalendarModal } from './closing-calendar-modal';
import { CustomEvent } from './custom-event';

// Has to be outside of component or it imediately rerenders on move or resize
const DragAndDropCalendar = withDragAndDrop(Calendar);

interface IClosingCalendar {
  myEvents: IClosingCalendarEvents[];
  setMyEvents: ReactSetState<any[]>;
  date: Date;
  setDate: ReactSetState<Date>;
  view: string;
  setView: ReactSetState<string>;
  isLoading: boolean;
  refetchEvents: () => void;
  isRefetching: boolean;
  shouldWarn: boolean;
  isConfirming: boolean;
  setIsConfirming: ReactSetState<boolean>;
  onSubmit?: () => void;
  handleICSEventDownload?: (event) => void;
}
export const ClosingCalendar: FC<IClosingCalendar> = ({
  myEvents,
  setMyEvents,
  date,
  setDate,
  view,
  setView,
  isLoading,
  refetchEvents,
  isRefetching,
  shouldWarn,
  onSubmit,
  isConfirming,
  setIsConfirming,
  handleICSEventDownload
}) => {
  const [startTime, setStartTime] = useState(moment().startOf('day').toDate());
  const [endTime, setEndTime] = useState(moment().endOf('day').toDate());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [step, setStep] = useState<number>(30);

  moment.tz.setDefault('America/Chicago');
  const localizer = momentLocalizer(moment);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const defaultView = isMobile ? 'day' : view;
  const isMonthView = view === Views.MONTH && !isMobile;

  const moveEvent = ({ event, start, end, isAllDay: droppedOnAllDaySlot = false }) => {
    const { allDay } = event;
    if (!allDay && droppedOnAllDaySlot) {
      event.allDay = true;
    }

    setMyEvents((prev: IGetCalendarEvent[]) => {
      const existing =
        prev.find(ev => ev.buildId === event.buildId && ev.eventType === event.eventType) ?? {};
      const filtered = prev.filter(ev => {
        const isSameBuildId = ev.buildId === event.buildId;
        const isSameEventType = ev.eventType === event.eventType;
        if (isSameBuildId && isSameEventType) {
          return false;
        }
        return true;
      });

      return [...filtered, { ...existing, start, end, allDay }];
    });
  };

  const resizeEvent = useCallback(
    ({ event, start, end }) => {
      setMyEvents((prev: IGetCalendarEvent[]) => {
        const existing =
          prev.find(ev => ev.buildId === event.buildId && ev.eventType === event.eventType) ?? {};
        const filtered = prev.filter(ev => {
          const isSameBuildId = ev.buildId === event.buildId;
          const isSameEventType = ev.eventType === event.eventType;
          if (isSameBuildId && isSameEventType) {
            return false;
          }
          return true;
        });
        return [...filtered, { ...existing, start, end }];
      });
    },
    [setMyEvents]
  );

  const handleSelectEvent = event => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleConfirmClose = () => {
    setIsConfirming(false);
  };

  const handleCalendarViewChange = action => {
    handleStateUpdateWithoutSave(
      shouldWarn,
      'You have unsaved changes. Are you sure you want to change the Calendar?',
      action
    );
  };

  const onViewChange = useCallback(
    view => {
      handleCalendarViewChange(setView(view));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setView]
  );

  function onNavigateChange(date) {
    handleCalendarViewChange(setDate(date));
  }

  const { components } = useMemo(
    () => ({
      components: {
        toolbar: props => (
          <CustomHeader
            date={props?.date}
            view={props?.view}
            views={props?.views}
            label={props?.label}
            onView={props?.onView}
            onNavigate={props?.onNavigate}
            setStep={setStep}
            step={step}
          />
        ),
        agenda: {
          event: event => (
            <CustomEvent
              event={event}
              action={event => {
                handleSelectEvent(event);
              }}
              isMonthView={isMonthView}
            />
          )
        },

        event: event => (
          <CustomEvent
            event={event}
            action={event => {
              handleSelectEvent(event);
            }}
            isMonthView={isMonthView}
          />
        )
      }
    }),
    [step, isMonthView]
  );

  const getTimeslots = useMemo(() => {
    return 60 / step;
  }, [step]);

  const getEventStyleProperties = (eventColor: string, textColor: 'white' | 'black' = 'white') => {
    return {
      backgroundColor: eventColor,
      color: textColor,
      border: `1px solid ${darken(eventColor, 0.15)}`
    };
  };

  const eventStyleFormatter = (eventType: string) => {
    switch (eventType) {
      case 'Closing Appointment':
        return getEventStyleProperties('#8bc640');
      case 'Homeowner Orientation':
        return getEventStyleProperties('#13274b');
      case 'Homeowner Celebration':
        return getEventStyleProperties('#ffc71f', 'black');
      default:
        return getEventStyleProperties('#8bc640');
    }
  };

  const eventStyleGetter = (event, start, end, isSelected) => {
    const eventStyle = eventStyleFormatter(event.eventType);

    // Conditionally set the minHeight based on isMonthView
    const additionalStyles = !isMonthView ? { minHeight: '3.5rem' } : {};

    const style = {
      backgroundColor: eventStyle.backgroundColor,
      color: eventStyle.color,
      border: eventStyle.border,
      display: 'block',
      ...additionalStyles
    };

    return {
      style: style
    };
  };

  return (
    <>
      {(isLoading || isRefetching) && <Loader type="fullscreen" position="centered" />}
      {!(isLoading || isRefetching) && (
        <Box sx={{ height: 780, marginTop: '1rem' }}>
          <DragAndDropCalendar
            defaultView={defaultView}
            date={date}
            events={myEvents}
            localizer={localizer}
            onEventDrop={moveEvent}
            onEventResize={resizeEvent}
            dayLayoutAlgorithm={'no-overlap'}
            onClickEvent={handleSelectEvent}
            onDoubleClickEvent={handleSelectEvent}
            components={components}
            onView={onViewChange}
            timeslots={getTimeslots}
            longPressThreshold={400}
            step={step}
            views={{
              week: false,
              month: WorkWeekMonthView,
              work_week: true,
              day: true
            }}
            eventPropGetter={eventStyleGetter}
            onNavigate={onNavigateChange}
            min={moment().set({ hour: 7 }).set({ minute: 0 }).toDate()}
            max={moment().set({ hour: 19 }).set({ minute: 0 }).toDate()}
            popup
            selectable
            resizable
          />
        </Box>
      )}
      <ClosingCalendarModal
        isOpen={isModalOpen}
        onClose={() => handleClose()}
        startDateTime={startTime}
        setStartDateTime={setStartTime}
        endDateTime={endTime}
        setEndDateTime={setEndTime}
        selectedEvent={selectedEvent}
        setCalendarDetail={setSelectedEvent}
        fetchCalendarEvents={refetchEvents}
        downloadEvent={handleICSEventDownload}
      />
      <ConfirmModal
        onConfirm={async () => {
          onSubmit();
          handleConfirmClose();
        }}
        onCancel={() => handleConfirmClose()}
        handleClose={() => handleConfirmClose()}
        isOpen={isConfirming}
      />
    </>
  );
};
